import { createContext, useEffect, useState } from "react";
import { serverTimestamp } from "firebase/firestore";
import { authService } from "@/domain/services/authService";
import { userRepository } from "@/data/repositories/userRepository";
import { User } from "@/models/User";
import { User as FirebaseUser } from "firebase/auth";
import { writeAuthDebug } from "./authDebug";

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  user: null,
  loading: true,
  isAdmin: false
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeUserDoc: (() => void) | undefined;

    // Surface redirect-flow errors (common on iOS/Safari) instead of failing silently.
    authService.consumeRedirectResult().catch((err: any) => {
      console.error("Redirect sign-in failed:", err);
      const code = err?.code as string | undefined;
      writeAuthDebug("consume_redirect_result:error", {
        errorCode: code,
        errorMessage: err?.message
      });
      const host =
        typeof window !== "undefined"
          ? window.location.hostname
          : "this domain";

      let message = "Sign in failed. Please try again.";

      if (code === "auth/unauthorized-domain") {
        message = `Sign in is blocked for ${host}. Add this host to Firebase Authentication -> Settings -> Authorized domains.`;
      } else if (code === "auth/web-storage-unsupported") {
        message =
          "Safari blocked web storage required for sign-in. Disable Private Browsing or content blocking and try again.";
      } else if (code === "auth/network-request-failed") {
        message =
          "Network error during sign-in. Check Safari connectivity and try again.";
      } else if (code === "auth/operation-not-allowed") {
        message =
          "Google sign-in is not enabled in Firebase Authentication -> Sign-in method.";
      } else if (code === "auth/popup-blocked") {
        message =
          "Your browser blocked the sign-in window. On mobile, open the site in Safari or Chrome and try again.";
      }

      if (typeof window !== "undefined") {
        window.alert(message);
      }
    });

    const unsubscribe = authService.subscribe(async fbUser => {
      writeAuthDebug("auth_state_changed", {
        firebaseUid: fbUser?.uid ?? null,
        firebaseEmail: fbUser?.email ?? null,
        detail: fbUser ? "Firebase auth state has a signed-in user." : "Firebase auth state is signed out."
      });
      setFirebaseUser(fbUser);

      if (!fbUser) {
        unsubscribeUserDoc?.();
        unsubscribeUserDoc = undefined;
        setUser(null);
        setLoading(false);
        return;
      }

      let appUser = await userRepository.getById(fbUser.uid);
      writeAuthDebug("user_doc_lookup", {
        firebaseUid: fbUser.uid,
        firebaseEmail: fbUser.email ?? null,
        detail: appUser ? "Found existing app user document." : "No app user document found yet."
      });

      // Redirect-based auth can sign in before user doc exists; create it if missing.
      if (!appUser) {
        await userRepository.create({
          id: fbUser.uid,
          email: fbUser.email ?? "",
          displayName: fbUser.displayName ?? "",
          photoURL: fbUser.photoURL ?? undefined,
          role: "player",
          userSettings: {
            theme: "classicCompact",
            autoAdvanceHole: false,
            focusMode: false,
            showNicknames: false,
            condenseScorecard: true,
            showActivityTicker: false,
            showEventPredictions: false
          },
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any
        } as User);

        appUser = await userRepository.getById(fbUser.uid);
        writeAuthDebug("user_doc_created", {
          firebaseUid: fbUser.uid,
          firebaseEmail: fbUser.email ?? null,
          detail: appUser ? "Created app user document after sign-in." : "Attempted to create app user document."
        });
      }

      unsubscribeUserDoc?.();
      unsubscribeUserDoc = userRepository.subscribe(fbUser.uid, nextUser => {
        setUser(nextUser);
      });

      setUser(appUser);
      writeAuthDebug("auth_provider:ready", {
        firebaseUid: fbUser.uid,
        firebaseEmail: fbUser.email ?? null,
        detail: appUser ? "Auth provider is ready with app user." : "Auth provider is ready but app user is still null."
      });
      setLoading(false);
    });

    return () => {
      unsubscribeUserDoc?.();
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        user,
        loading,
        isAdmin: user?.role === "admin"
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
